import { prefersReducedMotion } from '@theme/utilities';

/**
 * Manages sequential scroll-in animations for elements with classes:
 * - scroll-in-element
 * - scroll-in-element-two
 * - scroll-in-element-three
 * - scroll-in-element-from-left
 *
 * Animations are sequential: second waits for first, third waits for second,
 * scroll-in-element-from-left waits for scroll-in-element-three.
 * Desktop only (min-width: 990px).
 */
class ScrollInAnimations {
  constructor() {
    this.animationDuration = 600; // Match CSS animation duration in milliseconds
    this.isDesktop = window.innerWidth >= 990;
    this.observer = null;
    this.completedAnimations = new Set(); // Track which elements have completed animation

    // Only initialize on desktop and if motion is not reduced
    if (this.isDesktop && !prefersReducedMotion()) {
      this.init();
    }

    // Handle window resize to enable/disable on desktop breakpoint
    window.addEventListener('resize', this.#handleResize);
  }

  #handleResize = () => {
    const wasDesktop = this.isDesktop;
    this.isDesktop = window.innerWidth >= 990;

    // If switching from mobile to desktop, initialize
    if (!wasDesktop && this.isDesktop && !prefersReducedMotion()) {
      this.init();
    }
    // If switching from desktop to mobile, cleanup
    else if (wasDesktop && !this.isDesktop) {
      this.cleanup();
    }
  };

  init() {
    if (this.observer) return; // Already initialized

    const elements = {
      first: document.querySelectorAll('.scroll-in-element'),
      second: document.querySelectorAll('.scroll-in-element-two'),
      third: document.querySelectorAll('.scroll-in-element-three'),
      fromLeft: document.querySelectorAll('.scroll-in-element-from-left'),
    };

    // Create IntersectionObserver for scroll detection
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target instanceof HTMLElement && !entry.target.classList.contains('animate-in')) {
            this.#triggerAnimation(entry.target);
            // Unobserve after animation is triggered
            this.observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of element is visible
        rootMargin: '0px 0px -50px 0px', // Start animation slightly before element enters viewport
      }
    );

    // Observe all elements
    [...elements.first, ...elements.second, ...elements.third, ...elements.fromLeft].forEach((el) => {
      if (el) {
        this.observer.observe(el);
      }
    });
  }

  /**
   * Triggers animation for an element, handling sequential timing
   * @param {HTMLElement} element - The element to animate
   */
  #triggerAnimation(element) {
    const isFirst = element.classList.contains('scroll-in-element');
    const isSecond = element.classList.contains('scroll-in-element-two');
    const isThird = element.classList.contains('scroll-in-element-three');
    const isFromLeft = element.classList.contains('scroll-in-element-from-left');

    if (isFirst) {
      // First element animates immediately
      this.#animateElement(element);
    } else if (isSecond) {
      // Second element waits for first to complete
      this.#waitForPreviousAndAnimate(element, '.scroll-in-element');
    } else if (isThird) {
      // Third element waits for second to complete
      this.#waitForPreviousAndAnimate(element, '.scroll-in-element-two');
    } else if (isFromLeft) {
      // From-left element waits for third to complete
      this.#waitForPreviousAndAnimate(element, '.scroll-in-element-three');
    }
  }

  /**
   * Waits for previous element(s) to complete animation, then animates current element
   * @param {HTMLElement} element - The element to animate
   * @param {string} previousSelector - CSS selector for the previous element(s)
   */
  #waitForPreviousAndAnimate(element, previousSelector) {
    const previousElements = Array.from(document.querySelectorAll(previousSelector));
    
    // Filter to only visible previous elements that are in the viewport
    const visiblePreviousElements = previousElements.filter((el) => {
      const rect = el.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      return isVisible;
    });

    if (visiblePreviousElements.length === 0) {
      // No previous elements visible yet, wait a bit and check again
      setTimeout(() => {
        this.#waitForPreviousAndAnimate(element, previousSelector);
      }, 100);
      return;
    }

    // Check if all visible previous elements have completed their animation
    const allCompleted = visiblePreviousElements.every((el) => {
      // Check if element has animate-in class and animation has completed
      return el.classList.contains('animate-in') && this.completedAnimations.has(el);
    });

    if (!allCompleted) {
      // Wait for animations to complete
      setTimeout(() => {
        this.#waitForPreviousAndAnimate(element, previousSelector);
      }, 50);
      return;
    }

    // All previous elements have completed, animate this element
    this.#animateElement(element);
  }

  /**
   * Applies the animation class to an element and tracks when animation completes
   * @param {HTMLElement} element - The element to animate
   */
  #animateElement(element) {
    element.classList.add('animate-in');
    
    // Listen for animation end to mark as completed
    const handleAnimationEnd = (/** @type {AnimationEvent} */ e) => {
      if (
        e.target === element &&
        (e.animationName === 'fadeInFromAbove' || e.animationName === 'fadeInFromLeft')
      ) {
        this.completedAnimations.add(element);
        element.removeEventListener('animationend', handleAnimationEnd);
      }
    };
    
    element.addEventListener('animationend', handleAnimationEnd);
    
    // Fallback: mark as completed after animation duration
    setTimeout(() => {
      this.completedAnimations.add(element);
    }, this.animationDuration);
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ScrollInAnimations();
  });
} else {
  new ScrollInAnimations();
}

